import * as React from 'react';
import { PonderaModal, PVACharacteristicModal } from '../inclinationmodals';
import { InclinationChart, InclinationMidrangeChart, PVAContainer, DichotomyChart, PronouncedLabels, PVABadge } from '../../components';
import { Style, CharacteristicCompare, UserCharacteristicToggle } from '../../models';
import './GroupChart.css';
import { ReportType, InclinationType, MidRangeType } from '../../enums';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';


interface InclinationTeamMidRange {
    teamID: number;
    midRangeLow: number;
    midRangeHigh: number;
}
interface Inclination {
    id?: number;
    characteristicID?: number;
    name: string;
    helpText: string;
    description: string;
    tooltip: string;
    inclinationType: InclinationType;
    value: number;
    midrangeLow: number;
    midrangeHigh: number;
    midrangeLowOrg: number;
    midrangeHighOrg: number;
    midrangeLowAll: number;
    midrangeHighAll: number;
    teamMidRanges: InclinationTeamMidRange[];
}

interface SubGroup {
    name: string;
    sort: number;
    data: Inclination[];
    about: string;
}

interface GroupData {
    id?: number;
    characteristicGroupID?: number;
    name: string;
    subtitle: string;
    description: string;
    data: Inclination[];
    comparisons: CharacteristicCompare[];
    subGroups: SubGroup[];
}

interface GroupComparisonData {
    userId: number;
    data: {
        name: string;
        style: { name: string };
        inclinationGroups: {
            characteristicGroupID: number;
            data: {
                characteristicID: number;
                value: number;
            }[];
            subGroups?: {
                data: {
                    characteristicID: number;
                    value: number;
                }[];
            }[];
        }[];
    };
}

interface GroupChartProps {
    groupData: GroupData;
    style?: Style;
    reportType: ReportType;
    midRangeType: MidRangeType;
    teamID: number;
    index: number;
    offset: number;
    headerOffset?: boolean;
    small?: boolean; // needed to resize midrange charts properly
    allowToggle: boolean;
    comparisonData?: GroupComparisonData[];
    markedCharacteristics?: UserCharacteristicToggle[];
    onToggle?: (characteristicId: number, userId: number) => void;
    toggleState?: (characteristicId: number, userId: number) => boolean;
    userPermissions?: string[];
    preventInteraction?: boolean;
    skipAnimation?: boolean;
}

interface GroupChartState {
    visible: boolean;
    modalVisible: boolean[];
    groupModal: boolean;
}

export class GroupChart extends React.Component<GroupChartProps, GroupChartState> {
    constructor(props: GroupChartProps) {
        super(props);

        this.state = {
            visible: false,
            modalVisible: [false],
            groupModal: false
        };
    }

    toggleModal(index: number) {
        if (index === -1) {
            this.setState({ groupModal: !this.state.groupModal });
            return;
        }

        let { modalVisible } = this.state;

        modalVisible[index] = !modalVisible[index];
        this.setState({ modalVisible: modalVisible });
    }

    getGroupModalElement(name: string, core: string, content: string) {
        const currentStyleName = this.props.style ? this.props.style.name.toLowerCase() : '';
        const checkmarkUrl = currentStyleName ? `url("/images/checkmark-${currentStyleName}.svg")` : 'url("/images/checkmark.svg")';
        const checkMarkRedUrl = currentStyleName ? `url("/images/check-mark-${currentStyleName}.svg")` : 'url("/images/check mark red.svg")';

        const props = {
            show: this.state.groupModal,
            onHide: this.toggleModal.bind(this, -1),
            title: name,
            topic: core,
            content: content,
            activeColor: this.props.style ? `var(--pva-${this.props.style.name.toLowerCase()})` : undefined,
            customContentColor: this.props.style ? `var(--pva-${this.props.style.name.toLowerCase()})` : undefined,
            checkmarkUrl: checkmarkUrl,
            checkMarkRedUrl: checkMarkRedUrl
        };

        return <PonderaModal key="group-modal" {...props} />;
    }

    getModalElement(inclinationType: string, name: string, content: string, index: number, value: number, desc: string, group: string, core: string) {
        const currentStyleName = this.props.style ? this.props.style.name.toLowerCase() : '';
        const checkmarkUrl = currentStyleName ? `url("/images/checkmark-${currentStyleName}.svg")` : 'url("/images/checkmark.svg")';
        const checkMarkRedUrl = currentStyleName ? `url("/images/check-mark-${currentStyleName}.svg")` : 'url("/images/check mark red.svg")';

        const props = {
            show: this.state.modalVisible[index],
            onHide: this.toggleModal.bind(this, index),
            title: group,
            content: content,
            value: value,
            description: desc,
            core: core,
            step: this.props.index,
            name: name,
            allowToggle: this.props.allowToggle,
            activeColor: this.props.style ? `var(--pva-${this.props.style.name.toLowerCase()})` : undefined,
            customContentColor: this.props.style ? `var(--pva-${this.props.style.name.toLowerCase()})` : undefined,
            checkmarkUrl: checkmarkUrl,
            checkMarkRedUrl: checkMarkRedUrl
        };
        
        return <PVACharacteristicModal key={`modal-${index}`} {...props} />;
    }    
    
    getSimpleModalElement(name: string, core: string, content: string, index: number) {
        const currentStyleName = this.props.style ? this.props.style.name.toLowerCase() : '';
        const checkmarkUrl = currentStyleName ? `url("/images/checkmark-${currentStyleName}.svg")` : 'url("/images/checkmark.svg")';
        const checkMarkRedUrl = currentStyleName ? `url("/images/check-mark-${currentStyleName}.svg")` : 'url("/images/check mark red.svg")';

        const props = {
            show: this.state.modalVisible[index],
            onHide: this.toggleModal.bind(this, index),
            title: name,
            topic: core,
            content: content,
            activeColor: this.props.style ? `var(--pva-${this.props.style.name.toLowerCase()})` : undefined,
            customContentColor: this.props.style ? `var(--pva-${this.props.style.name.toLowerCase()})` : undefined,
            checkmarkUrl: checkmarkUrl,
            checkMarkRedUrl: checkMarkRedUrl
        };

        return <PonderaModal key={`simple-modal-${index}`} {...props} />;
    }

    private getMidrangeValue(item: Inclination) {
        let midrangeLow = item.midrangeLow;
        let midrangeHigh = item.midrangeHigh;
        switch (this.props.midRangeType) {
            case MidRangeType.Team: {
                const mid = item.teamMidRanges.find((t: InclinationTeamMidRange) => t.teamID === this.props.teamID);
                if (mid) {
                    midrangeLow = mid.midRangeLow;
                    midrangeHigh = mid.midRangeHigh;
                }
                break;
            }
            case MidRangeType.Organization: {
                midrangeLow = item.midrangeLowOrg;
                midrangeHigh = item.midrangeHighOrg;
                break;
            }
            case MidRangeType.All: {
                midrangeLow = item.midrangeLowAll;
                midrangeHigh = item.midrangeHighAll;
                break;
            }
        }
        return { midrangeLow, midrangeHigh };
    }

    private renderChart(item: Inclination, currIndex: number, characteristicID?: number, userDataIndex?: number) {
        const { reportType, style, small, allowToggle, comparisonData, onToggle, toggleState, groupData } = this.props;

        if (comparisonData && comparisonData.length > 0) {
            if (userDataIndex !== undefined) {
                const userData = comparisonData[userDataIndex];
                let scoreValue = 0;
                if (userData.data && userData.data.inclinationGroups) {
                    const gData = userData.data.inclinationGroups.find((x) => x.characteristicGroupID === groupData.characteristicGroupID || x.characteristicGroupID === groupData.id);
                    if (gData) {
                        let scoreData = gData.data.find((inclination) => inclination.characteristicID === characteristicID);
                        if (!scoreData && gData.subGroups) {
                            const subGroup = gData.subGroups.find((sg) => sg.data.some((inclination) => inclination.characteristicID === characteristicID));
                            if (subGroup) {
                                scoreData = subGroup.data.find((inclination) => inclination.characteristicID === characteristicID);
                            }
                        }
                        if (scoreData) {
                            scoreValue = scoreData.value;
                        }
                    }
                }

                return (
                    <div className="cell" key={`grid-chart-${userDataIndex}`}>
                        <InclinationChart
                            value={scoreValue}
                            allowToggle={allowToggle}
                            onToggle={() => onToggle && characteristicID && onToggle(characteristicID, userData.userId)}
                            initialClickedState={toggleState && characteristicID ? toggleState(characteristicID, userData.userId) : false}
                            skipAnimation={this.props.skipAnimation}
                        />
                    </div>
                );
            }
            return (
                <div>
                    {comparisonData.map((userData: GroupComparisonData, userDataIndex: number) => {
                        let scoreValue = 0;
                        if (userData.data && userData.data.inclinationGroups) {
                            const gData = userData.data.inclinationGroups.find((x) => x.characteristicGroupID === groupData.characteristicGroupID || x.characteristicGroupID === groupData.id);
                            if (gData) {
                                let scoreData = gData.data.find((inclination) => inclination.characteristicID === characteristicID);
                                if (!scoreData && gData.subGroups) {
                                    const subGroup = gData.subGroups.find((sg) => sg.data.some((inclination) => inclination.characteristicID === characteristicID));
                                    if (subGroup) {
                                        scoreData = subGroup.data.find((inclination) => inclination.characteristicID === characteristicID);
                                    }
                                }
                                if (scoreData) {
                                    scoreValue = scoreData.value;
                                }
                            }
                        }

                        return (
                            <div className="cell" key={`grid-chart-${userDataIndex}`}>
                                <InclinationChart
                                    value={scoreValue}
                                    allowToggle={allowToggle}
                                    onToggle={() => onToggle && characteristicID && onToggle(characteristicID, userData.userId)}
                                    initialClickedState={toggleState && characteristicID ? toggleState(characteristicID, userData.userId) : false}
                                    skipAnimation={this.props.skipAnimation}
                                />
                            </div>
                        );
                    })}
                </div>
            );
        }

        let chart;
        if (reportType === ReportType.Midrange) {
            const { midrangeLow, midrangeHigh } = this.getMidrangeValue(item);
            chart = <InclinationMidrangeChart value={item.value} midrangeLow={midrangeLow} midrangeHigh={midrangeHigh} style={style ? style.name : ''} small={small} skipAnimation={this.props.skipAnimation} />;
        } else {
            chart = <InclinationChart value={Math.round(item.value)} allowToggle={allowToggle} skipAnimation={this.props.skipAnimation} />;
        }
        return chart;
    }

    private InclinationRow(item: Inclination, index: number, modalIndex: number, groupName: string, groupSubtitle: string, modals: any[], isWide: boolean) {
        const tooltip = (
            <Tooltip id={`tooltip-inclination-${modalIndex}`}>
                <div dangerouslySetInnerHTML={{ __html: item.tooltip || item.helpText }} />
            </Tooltip>
        );

        modals.push(this.getModalElement(
            item.inclinationType.toString(),
            item.name,
            item.helpText,
            modalIndex,
            item.value,
            item.description,
            groupName,
            groupSubtitle
        ));

        return (
            <>
                <span className="characteristic-index">{index}.</span>
                <span className="characteristic-info icon-button" onClick={this.toggleModal.bind(this, modalIndex)}>
                    {!this.props.preventInteraction && <img src="/images/inf tooltip.svg" />}
                </span>
                <span onClick={this.toggleModal.bind(this, modalIndex)} className="characteristic-title cursor-pointer">
                    <OverlayTrigger animation={false} trigger={['hover', 'focus']} delay={0} placement="top" overlay={tooltip}>
                        <b>{item.name}</b>
                    </OverlayTrigger>
                </span>
            </>
        );
    }
    
    private CreateComparisonRows(data: Inclination[], groupName: string, groupSubtitle: string, modals: any[], offset: number, startModalIndex: number) {
        const { comparisonData } = this.props;

        return data.map((item, index) => {
            const characteristicID = item.characteristicID || item.id;
            return (
                <>
                    <div className="chart-row" key={characteristicID || index}>
                        <div className="characteristic-text">
                            {this.InclinationRow(item, index + offset + 1, index + startModalIndex, groupName, groupSubtitle, modals, false)}
                        </div>
                        <div/>
                    </div>
                    <div className="user-score-rows">
                        {comparisonData && comparisonData.filter(comparison => comparison.data && comparison.data.name).map((comparison, userIndex) => {
                            const s = (comparison.data && comparison.data.style) ? comparison.data.style.name : ' ';
                            return (
                                <div className={"user-score-row index-" + userIndex} key={userIndex}>
                                    <div key={`user-comparison-header-${userIndex}`} className="user-score-name">
                                        <span>{comparison.data.name}</span>
                                        <PVABadge style={s} badge={s[0]} />
                                    </div>
                                    <div className="characteristic-chart">
                                        {this.renderChart(item, index, characteristicID, userIndex)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            );
        });
    }
    
    private CreateNonComparisonRows(data: Inclination[], groupName: string, groupSubtitle: string, modals: any[], offset: number, startModalIndex: number) {
        return data.map((item, index) => {
            const characteristicID = item.characteristicID || item.id;
            return <div className={"chart-row index-" + index} key={characteristicID || index}>
                <div className="characteristic-text">
                    {this.InclinationRow(item, index + offset + 1, index + startModalIndex, groupName, groupSubtitle, modals, false)}
                </div>
                <div className="characteristic-chart">
                    {this.renderChart(item, index, characteristicID)}
                </div>
            </div>
        });
    }

    private CreateRows(data: Inclination[], groupName: string, groupSubtitle: string, modals: any[], offset: number, startModalIndex: number) {
        if (this.props.comparisonData && this.props.comparisonData.length > 0) return this.CreateComparisonRows(data, groupName, groupSubtitle, modals, offset, startModalIndex);
        return this.CreateNonComparisonRows(data, groupName, groupSubtitle, modals, offset, startModalIndex);
    }

    private SingleAreaInclinationTable(groupName: string, groupSubtitle: string, data: Inclination[], modals: any[], areaName: string, offset: number, startModalIndex: number) {
        return (
            <div className="chart-container single-area">
                {!this.props.preventInteraction && <PronouncedLabels />}
                {this.props.preventInteraction && <div />}
                {this.CreateRows(data, groupName, groupSubtitle, modals, offset, startModalIndex)}
            </div>
        );
    }

    private MultiAreaInclinationTable(groupName: string, groupSubtitle: string, data: Inclination[], modals: any[], areaName: string, about: string, offset: number, groupModalIndex: number, startModalIndex: number) {
        modals.push(this.getSimpleModalElement(areaName, '', about, groupModalIndex));

        const singleAreaIds = this.props.groupData.data.map(item => item.characteristicID || item.id);
        const filteredData = data.filter(item => {
            const itemId = item.characteristicID || item.id;
            return singleAreaIds.indexOf(itemId) === -1;
        });

        if (filteredData.length === 0) {
            return null;
        }

        return (
            <div className="chart-container multi-area">
                <div className="area-name font-second-level">{areaName}</div>
                {this.CreateRows(filteredData, groupName, groupSubtitle, modals, offset, startModalIndex)}
            </div>
        );
    }

    render() {
        const { groupData, index } = this.props;

        let modals: any[] = [];
        let modalIndexCounter = this.props.offset;

        const groupTooltip = (
            <Tooltip id="tooltip-group-info">
                <div dangerouslySetInnerHTML={{ __html: groupData.description }} />
            </Tooltip>
        );

        const title = (
            <div>
                <div className="title-sub">{groupData.subtitle}</div>
                <span>
                    <OverlayTrigger animation={false} trigger={['hover', 'focus']} delay={0} placement="top" overlay={groupTooltip}>
                        <span onClick={this.toggleModal.bind(this, -1)}> {groupData.name}</span>
                    </OverlayTrigger>
                    &nbsp;&nbsp;
                    <span className="icon-button" onClick={this.toggleModal.bind(this, -1)}>
                        <img src="/images/inf tooltip.svg" />
                    </span>
                </span>
            </div>
        );

        const comparisonsContent = (
            <div className="dichotomy-container">
            { this.props.groupData.comparisons && this.props.groupData.comparisons.sort((a: any, b: any) => {
                    if (a.sort > b.sort) {
                        return 1;
                    } else if (a.sort < b.sort) {
                        return -1;
                    }
                    return 0;
                }).map((cc: any, ind: number) => {
                    let score: number = 0;
                    if (cc.characteristicCompareScoreType === 'thresholdGreater') {
                        if (cc.characteristicCompareScoreSide === 'left') {
                            // if left side is greater than threshold use left score
                            if (cc.characteristicLeftScore > cc.threshold) {
                                score = -cc.characteristicLeftScore;
                            } else {
                                if (cc.characteristicRightScore > cc.characteristicLeftScore) {
                                    score = cc.characteristicRightScore;
                                } else {
                                    score = -cc.characteristicLeftScore;
                                }
                            }
                        } else {
                            // if right side is greater than threshold use left score
                            if (cc.characteristicRightScore > cc.threshold) {
                                score = cc.characteristicRightScore;
                            } else {
                                if (cc.characteristicRightScore > cc.characteristicLeftScore) {
                                    score = cc.characteristicRightScore;
                                } else {
                                    score = -cc.characteristicLeftScore;
                                }
                            }
                        }
                    } else if (cc.characteristicCompareScoreType === 'thresholdLessThan') {
                        if (cc.characteristicCompareScoreSide === 'left') {
                            // if left side is greater than threshold use left score
                            if (cc.characteristicLeftScore < cc.threshold) {
                                score = -cc.characteristicLeftScore;
                            } else {
                                score = cc.characteristicRightScore;
                            }
                        } else {
                            // if right side is greater than threshold use left score
                            if (cc.characteristicRightScore < cc.threshold) {
                                score = cc.characteristicRightScore;
                            } else {
                                score = -cc.characteristicLeftScore;
                            }
                        }
                    } else 
                    if (cc.characteristicCompareScoreType === 'forceGreater') {
                        if (cc.characteristicCompareScoreSide === 'left') {
                            // if left side is greater than threshold use left score
                            if (cc.characteristicLeftScore > cc.threshold) {
                                score = -cc.characteristicLeftScore;
                            } else {
                                score = cc.characteristicRightScore;
                            }
                        } else {
                            // if right side is greater than threshold use left score
                            if (cc.characteristicRightScore > cc.threshold) {
                                score = cc.characteristicRightScore;
                            } else {
                                score = -cc.characteristicLeftScore;
                            }
                        }
                    } else if (cc.characteristicCompareScoreType === 'forceLessThan') {
                        if (cc.characteristicCompareScoreSide === 'left') {
                            // if left side is greater than threshold use left score
                            if (cc.characteristicLeftScore < cc.threshold) {
                                score = -cc.characteristicLeftScore;
                            } else {
                                score = cc.characteristicRightScore;
                            }
                        } else {
                            // if right side is greater than threshold use left score
                            if (cc.characteristicRightScore < cc.threshold) {
                                score = cc.characteristicRightScore;
                            } else {
                                score = -cc.characteristicLeftScore;
                            }
                        }
                    } else {
                        score = cc.characteristicLeftScore > cc.characteristicRightScore ?  -cc.characteristicLeftScore : cc.characteristicRightScore;
                    }

                    return <div key={`cc-${ind}`} className="dichotomy-row">
                        <div className="row">
                            <div className="col-xs-3 text-right dichotomy-content">
                                <div dangerouslySetInnerHTML={{__html: cc.left}} />
                            </div>
                            <div className="col-xs-6 text-center">
                                <div className="dichotomy-title">
                                    <div className="dichotomy-left">
                                        PRONOUNCED
                                    </div>
                                    <div className="dichotomy-center">
                                        {cc.name}
                                    </div>
                                    <div className="dichotomy-right">
                                        PRONOUNCED
                                    </div>
                                </div>
                                <div>
                                    <DichotomyChart value={score} style={(this.props.style  && this.props.style.name) ? this.props.style.name : undefined} />
                                </div>
                            </div>
                            <div className="col-xs-3 text-left dichotomy-content">
                                <div dangerouslySetInnerHTML={{__html: cc.right}} />
                            </div>
                        </div>
                    </div>;
                })
            }
        </div>);

        let offset = this.props.offset;

        modals.push(this.getGroupModalElement(groupData.name, groupData.subtitle, groupData.description));

        const content = (
            <>
                <>
                    {groupData.data.length > 0 && this.SingleAreaInclinationTable(groupData.name, groupData.subtitle, groupData.data, modals, '', offset, modalIndexCounter)}
                    {(() => {
                        modalIndexCounter += groupData.data.length;
                        offset += groupData.data.length;
                        return groupData.subGroups.sort((sg1: any, sg2: any) => {
                            if (sg1.sort > sg2.sort) {
                                return 1;
                            }
                            if (sg1.sort < sg2.sort) {
                                return -1;
                            }
                            return 0;
                        }).map((sg: SubGroup, sbInd: number) => {
                            const groupModalIndex = modalIndexCounter;
                            const startModalIndex = modalIndexCounter + 1;
                            const currentOffset = offset;
                            
                            modalIndexCounter += sg.data.length + 1;
                            offset += sg.data.length;

                            return this.MultiAreaInclinationTable(groupData.name, groupData.subtitle, sg.data, modals, sg.name, sg.about, currentOffset, groupModalIndex, startModalIndex);
                        });
                    })()}
                </>

                {/*{this.props.groupData.comparisons && this.props.groupData.comparisons.length > 0 && comparisonsContent}*/}

                {modals}
            </>
        );

        const icon = (this.props.style && this.props.style.name) ? `badges/${this.props.style.name.toLowerCase()} icon.svg` : undefined;

        const tooltipData = {topic: groupData.name, title: groupData.subtitle, content: groupData.description};
        
        return (
            <div className="component-PVAGroupChart">
                <PVAContainer topic={groupData.subtitle} title={groupData.name} id={groupData.name} offset={this.props.headerOffset} tooltipData={tooltipData} preventInteraction={this.props.preventInteraction}>
                    {icon && this.props.style && <PVAContainer.Icon>
                        <div className={`icon`}><img src={`/images/${icon}`} alt="" /></div>
                    </PVAContainer.Icon>}
                    {content}
                </PVAContainer>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/charts/GroupChart.tsx